import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftRight, CheckCircle2, Repeat, TrendingUp, Zap } from 'lucide-react';
import { toast } from 'sonner';
import {
  ActionButton,
  ContextHero,
  DataCard,
  DateBadge,
  EmptyStateModern,
  KpiCard,
  MoneyAmount,
  PageContainer,
  SectionTitle,
  SkeletonShimmer,
  StatusBadge,
  type UniversalStatus,
} from '@/components/design-system';
import {
  useAvancesInterActivites,
  useCompenserAvances,
  usePreviewCompensations,
  useUpdateAvanceStatut,
} from '@/hooks/queries/useCaisseGroupe';
import { useAllActivites } from '@/hooks/queries/useActivites';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const STATUT_TONE: Record<string, UniversalStatus> = {
  a_rembourser: 'a_payer',
  rembourse_partiel: 'en_cours',
  rembourse: 'paye',
  annule: 'annule',
};

export function AvancesInterActivitesTab(): JSX.Element {
  const { data: avances = [], isLoading } = useAvancesInterActivites();
  const { data: activites = [] } = useAllActivites();
  const { data: compensations = [] } = usePreviewCompensations();
  const compenser = useCompenserAvances();
  const updateStatut = useUpdateAvanceStatut();
  const [compensationOpen, setCompensationOpen] = useState(false);

  const actMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of activites) m.set(a.id, a.nom);
    return m;
  }, [activites]);

  const enCours = avances.filter(
    (a) => a.statut === 'a_rembourser' || a.statut === 'rembourse_partiel',
  );
  const totalEnCours = enCours.reduce(
    (s, a) => s + (Number(a.montant) - Number(a.montant_rembourse)),
    0,
  );
  const plusAncienne = enCours
    .slice()
    .sort((a, b) => new Date(a.date_avance).getTime() - new Date(b.date_avance).getTime())[0];
  const joursDepuis = plusAncienne
    ? Math.floor((Date.now() - new Date(plusAncienne.date_avance).getTime()) / 86400000)
    : 0;

  // Solde net par activité (prêté - emprunté)
  const soldeNet = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of enCours) {
      const restant = Number(a.montant) - Number(a.montant_rembourse);
      m.set(a.activite_emettrice_id, (m.get(a.activite_emettrice_id) ?? 0) + restant);
      m.set(a.activite_beneficiaire_id, (m.get(a.activite_beneficiaire_id) ?? 0) - restant);
    }
    return [...m.entries()].sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]));
  }, [enCours]);

  const handleRembourser = async (
    id: string,
    montantTotal: number,
    restant: number,
  ): Promise<void> => {
    try {
      await updateStatut.mutateAsync({
        id,
        statut: 'rembourse',
        montant_rembourse: montantTotal,
      });
      toast.success(`Avance soldée (${restant.toLocaleString('fr-DZ')} DA)`);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <PageContainer>
      <div className="space-y-6">
        <ContextHero
          variant="finances"
          icon={<ArrowLeftRight className="h-5 w-5" />}
          title="Avances inter-activités"
          subtitle="Quand une activité paie pour une autre, l'avance est tracée automatiquement et apparaît ici jusqu'au remboursement."
          actions={
            compensations.length > 0 ? (
              <ActionButton
                variant="gradient"
                module="finances"
                icon={<Zap className="h-4 w-4" />}
                onClick={() => setCompensationOpen(true)}
              >
                Compenser auto ({compensations.length})
              </ActionButton>
            ) : undefined
          }
        />

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard
            variant="finances"
            label="Total avances en cours"
            value={<MoneyAmount amount={totalEnCours} variant="inline" />}
            icon={<Repeat className="h-4 w-4" />}
            loading={isLoading}
          />
          <KpiCard
            variant="finances"
            label="Plus ancienne"
            value={plusAncienne ? `${joursDepuis} j` : '—'}
            hint={plusAncienne?.reference}
            loading={isLoading}
          />
          <KpiCard
            variant="echeances"
            label="Activité prêteuse"
            value={
              soldeNet[0]?.[1] && soldeNet[0][1] > 0 ? (actMap.get(soldeNet[0][0]) ?? '—') : '—'
            }
            hint={
              soldeNet[0]?.[1] && soldeNet[0][1] > 0
                ? `Solde net : ${new Intl.NumberFormat('fr-DZ').format(soldeNet[0][1])} DA`
                : undefined
            }
            loading={isLoading}
          />
          <KpiCard
            variant="finances"
            label="Activité emprunteuse"
            value={(() => {
              const last = soldeNet[soldeNet.length - 1];
              if (!last || last[1] >= 0) return '—';
              return actMap.get(last[0]) ?? '—';
            })()}
            loading={isLoading}
          />
        </div>

        {soldeNet.length > 0 && (
          <DataCard title="Solde net par activité (en cours)">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {soldeNet.map(([activiteId, solde]) => (
                <div
                  key={activiteId}
                  className={
                    'rounded-xl p-3 ring-1 ' +
                    (solde > 0
                      ? 'bg-emerald-50 ring-emerald-200'
                      : solde < 0
                        ? 'bg-rose-50 ring-rose-200'
                        : 'bg-slate-50 ring-slate-200')
                  }
                >
                  <div className="text-xs font-semibold text-slate-700">
                    {actMap.get(activiteId) ?? '—'}
                  </div>
                  <div className="mt-1 flex items-center justify-between">
                    <MoneyAmount amount={solde} variant="inline" withColor showSign />
                    <span className="text-[10px] text-slate-500">
                      {solde > 0 ? 'a prêté' : solde < 0 ? 'doit' : 'équilibré'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </DataCard>
        )}

        <SectionTitle>
          {avances.length} avance{avances.length > 1 ? 's' : ''}
        </SectionTitle>

        {isLoading ? (
          <SkeletonShimmer variant="table" count={5} />
        ) : avances.length === 0 ? (
          <EmptyStateModern
            variant="finances"
            icon={<ArrowLeftRight className="h-7 w-7" />}
            title="Aucune avance inter-activités"
            description="Les avances sont créées automatiquement quand vous saisissez une opération avec une activité émettrice différente de l'activité bénéficiaire."
          />
        ) : (
          <DataCard>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-left text-[10px] uppercase tracking-widest text-slate-400">
                  <tr>
                    <th className="py-2">Référence</th>
                    <th className="py-2">Date</th>
                    <th className="py-2">Émettrice</th>
                    <th className="py-2">Bénéficiaire</th>
                    <th className="py-2">Motif</th>
                    <th className="py-2 text-right">Montant</th>
                    <th className="py-2 text-right">Restant</th>
                    <th className="py-2">Statut</th>
                    <th className="py-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {avances.map((a, i) => {
                    const restant = Number(a.montant) - Number(a.montant_rembourse);
                    return (
                      <motion.tr
                        key={a.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: Math.min(i, 8) * 0.02 }}
                        className="border-t border-slate-100"
                      >
                        <td className="py-2 font-mono">{a.reference}</td>
                        <td className="py-2">
                          <DateBadge date={a.date_avance} />
                        </td>
                        <td className="py-2 text-slate-700">
                          {actMap.get(a.activite_emettrice_id) ?? '—'}
                        </td>
                        <td className="py-2 text-slate-700">
                          {actMap.get(a.activite_beneficiaire_id) ?? '—'}
                        </td>
                        <td className="line-clamp-1 py-2 text-slate-600">{a.motif ?? '—'}</td>
                        <td className="py-2 text-right">
                          <MoneyAmount amount={a.montant} variant="inline" />
                        </td>
                        <td className="py-2 text-right">
                          <MoneyAmount amount={restant} variant="inline" withColor />
                        </td>
                        <td className="py-2">
                          <StatusBadge
                            status={STATUT_TONE[a.statut] ?? 'en_attente'}
                            label={a.statut.replace(/_/g, ' ')}
                          />
                        </td>
                        <td className="py-2 text-right">
                          {(a.statut === 'a_rembourser' || a.statut === 'rembourse_partiel') && (
                            <ActionButton
                              size="sm"
                              variant="gradient"
                              module="echeances"
                              loading={updateStatut.isPending}
                              onClick={() =>
                                void handleRembourser(a.id, Number(a.montant), restant)
                              }
                              icon={<CheckCircle2 className="h-3 w-3" />}
                            >
                              Rembourser
                            </ActionButton>
                          )}
                        </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </DataCard>
        )}

        <span className="hidden">
          <TrendingUp />
        </span>
      </div>

      {/* Modal compensation auto avances symétriques */}
      <Dialog open={compensationOpen} onOpenChange={setCompensationOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Compensation automatique des avances symétriques</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-3">
            {compensations.length === 0 ? (
              <div className="py-3 text-center text-sm text-slate-500">
                Aucune compensation possible — pas de paire d&apos;activités avec dettes
                réciproques.
              </div>
            ) : (
              <>
                <p className="text-xs text-slate-600">
                  Les avances suivantes seront soldées par compensation (une activité doit à
                  l&apos;autre, et inversement) :
                </p>
                <ul className="space-y-2">
                  {compensations.map((c, i) => (
                    <li key={i} className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-bold text-slate-800">
                          {actMap.get(c.activite_a_id) ?? c.activite_a_id} ↔{' '}
                          {actMap.get(c.activite_b_id) ?? c.activite_b_id}
                        </span>
                        <MoneyAmount amount={c.montant_compensable} variant="inline" />
                      </div>
                      <div className="mt-1 text-[10px] text-slate-500">
                        {c.avances_a_solder.length} avance(s) à solder
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="rounded-lg bg-amber-50 p-2 text-xs text-amber-800">
                  ℹ️ Cette action marque définitivement les avances comme remboursées (date
                  d&apos;aujourd&apos;hui). Elle est tracée dans l&apos;audit log.
                </div>
              </>
            )}
          </DialogBody>
          <DialogFooter>
            <ActionButton type="button" variant="ghost" onClick={() => setCompensationOpen(false)}>
              Annuler
            </ActionButton>
            <ActionButton
              variant="gradient"
              module="finances"
              loading={compenser.isPending}
              disabled={compensations.length === 0}
              icon={<Zap className="h-4 w-4" />}
              onClick={async () => {
                try {
                  const r = await compenser.mutateAsync(compensations);
                  toast.success(`${r.count} avance(s) soldée(s) par compensation`);
                  setCompensationOpen(false);
                } catch (e) {
                  toast.error((e as Error).message);
                }
              }}
            >
              Confirmer la compensation
            </ActionButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
